import React, { useEffect, useMemo, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import WavingHandUrl from '@pkg/assets/images/Waving_Hand.png';
import { FieldEmail, type FieldEmailProps, FieldPassword, type FieldPasswordProps } from '@pkg/components';
import { useRegister } from '@pkg/hooks';

import { useAuthSidebar } from '@app/store/zustand';

import { Checkbox } from '../../Checkbox';
import { Typography } from '../../DesignSystem';
import { DepButton } from '../../deprecated';
import {
    Container,
    EmojiImage,
    FormGroup,
    FormRow,
    FormRowReset,
    PrimaryCopySection,
    Section,
} from '../Web3Auth.style';
import { AuthNewLocalStorage, Web3AuthSteps } from '../Web3Auth.types';
import { FormMessage } from '../components';
import { useWeb3AuthContext } from '../context';

// TODO: We should have a real source of truth
// for what our error responses are.
const KNOWN_ERRORS = {
    email: 'Email invalid format',
    password: 'Password invalid',
    repassword: 'Passwords do not match.',
};

export function SignUpWithEmail() {
    const [translate] = useTranslation(['auth', 'common']);

    const { setAuthEmail, setCurrentStep } = useWeb3AuthContext();
    const { postAuthRedirect } = useAuthSidebar();

    const { register } = useRegister();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [repassword, setRePassword] = useState('');
    const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(false);

    const [loading, setLoading] = useState(false);
    const [hasAuthError, setHasAuthError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleEmailChange: FieldEmailProps['onChange'] = (incomingValue) => {
        setEmail(`${incomingValue ?? ''}`);
    };

    const handlePasswordChange: FieldPasswordProps['onChange'] = (incomingValue) => {
        setPassword(`${incomingValue ?? ''}`);
    };

    const handleRePasswordChange: FieldPasswordProps['onChange'] = (incomingValue) => {
        setRePassword(`${incomingValue ?? ''}`);
    };

    async function handleSignUp() {
        setLoading(true);
        setAuthEmail(email);

        try {
            await register({
                email,
                password,
                subscribed: subscribeToNewsletter,
                redirect: postAuthRedirect,
            });

            localStorage.setItem(AuthNewLocalStorage.OAuthLoggedIn, '1');
            setCurrentStep(Web3AuthSteps.EmailActivation);
            setLoading(false);
        } catch {
            setLoading(false);
            setHasAuthError(true);
        }
    }

    const signUpDisabled = useMemo(() => {
        const anyFieldsEmpty = !email || !password || !repassword;
        const inequivalentPasswords = password !== repassword;

        return anyFieldsEmpty || inequivalentPasswords || hasAuthError;
    }, [email, password, repassword, hasAuthError]);

    const hasError = Boolean(hasAuthError && errorMessage);
    const hasEmailError = hasError && errorMessage === KNOWN_ERRORS.email;
    const hasPasswordError =
        hasError && (errorMessage === KNOWN_ERRORS.password || errorMessage === KNOWN_ERRORS.repassword);

    useEffect(() => {
        if (hasAuthError) setHasAuthError(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, password]);

    useEffect(() => {
        if (password && repassword && password !== repassword) {
            setHasAuthError(true);
            setErrorMessage(KNOWN_ERRORS.repassword);
        } else {
            setHasAuthError(false);
        }
    }, [password, repassword]);

    const errorMarkup = hasError ? <FormMessage key="FormMessage" message={errorMessage} autoScroll error /> : null;

    return (
        <Container>
            <Section className="hide-on-tablet">
                <EmojiImage src={WavingHandUrl} />
            </Section>

            <PrimaryCopySection>
                <Typography type="headline" size={40}>
                    Sign up with email
                </Typography>
                <Typography type="paragraph" size={18} faded={true}>
                    Use an email and password to access your Yat account.
                </Typography>
            </PrimaryCopySection>

            <Section>
                <FormGroup>
                    <FormRow>
                        <FieldEmail
                            autoFocus
                            label={translate('common:email')}
                            value={email}
                            error={hasEmailError}
                            onChange={handleEmailChange}
                        />
                    </FormRow>

                    <FormRow>
                        <FieldPassword
                            label={translate('common:password')}
                            value={password}
                            error={hasPasswordError}
                            onChange={handlePasswordChange}
                        />
                    </FormRow>

                    <FormRow>
                        <FieldPassword
                            label={translate('common:repassword')}
                            value={repassword}
                            error={hasPasswordError}
                            onChange={handleRePasswordChange}
                        />
                    </FormRow>

                    <FormRow>
                        <FormRowReset>
                            <AnimatePresence>{errorMarkup}</AnimatePresence>

                            <Checkbox
                                id="checkbox-optin"
                                wrapperClass="waitlist-email__optin-check"
                                checked={subscribeToNewsletter}
                                onChange={() => setSubscribeToNewsletter((current) => !current)}
                            >
                                <Typography type="paragraph">
                                    Subscribe for the latest Yat tips & crypto steals. No junk mail! 💌
                                </Typography>
                            </Checkbox>
                        </FormRowReset>
                    </FormRow>

                    <FormRow>
                        <DepButton variant="glow" disabled={signUpDisabled} loading={loading} onClick={handleSignUp}>
                            {translate('common:sign_up')}
                        </DepButton>
                    </FormRow>
                </FormGroup>
            </Section>
        </Container>
    );
}
